import { getApiConfig } from "./config.api";

interface ApiResponse<T = any> {
    data: T;
    token?: string;
    message?: string;
    error?: string;
    state: boolean;
}

interface RequestConfig extends RequestInit {
    skipAuth?: boolean;
    skipErrorRedirect?: boolean;
    timeout?: number;
}

class ApiClient {
    private baseURL: string;
    private defaultTimeout: number = 20000;

    constructor() {
        this.baseURL = getApiConfig().BASE_URL;
    }

    async request<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
        const { skipAuth = false, skipErrorRedirect = false, timeout = this.defaultTimeout, ...requestConfig } = config;
        const url = `${this.baseURL}${endpoint}`;
        const token = localStorage.getItem('token');
        const isFormData = requestConfig.body instanceof FormData;
        const headers: Record<string, string> = {
            ...(requestConfig.headers as Record<string, string>),
        };

        if (!isFormData) {
            headers['Content-Type'] = 'application/json';
        }

        if (!skipAuth && token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const finalConfig: RequestInit = { ...requestConfig, headers };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        finalConfig.signal = controller.signal;

        try {
            const response = await fetch(url, finalConfig);
            clearTimeout(timeoutId);

            if (response.status === 401 && !skipAuth) {
                this.handleAuthError();
                throw new Error('Sesión expirada');
            }

            if (response.status === 500 && !skipErrorRedirect) {
                this.handleServerError(response);
                throw new Error('Error interno del servidor');
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `Error ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error(`Timeout: La petición tardó más de ${timeout}ms`);
                }
                throw error;
            }
            throw new Error('Error de conexión');
        }
    }

    /**
     * Igual que request() pero devuelve un Blob en vez de parsear JSON.
     * Útil para descargar archivos (xlsx, pdf, csv, etc.).
     */
    async requestBlob(endpoint: string, config: RequestConfig = {}): Promise<{ blob: Blob; filename: string }> {
        const { skipAuth = false, timeout = this.defaultTimeout, ...requestConfig } = config;
        const url = `${this.baseURL}${endpoint}`;
        const token = localStorage.getItem('token');

        const headers: Record<string, string> = {
            ...(requestConfig.headers as Record<string, string>),
        };

        if (!skipAuth && token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const finalConfig: RequestInit = { ...requestConfig, headers };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        finalConfig.signal = controller.signal;

        try {
            const response = await fetch(url, finalConfig);
            clearTimeout(timeoutId);

            if (response.status === 401 && !skipAuth) {
                this.handleAuthError();
                throw new Error('Sesión expirada');
            }

            if (!response.ok) {
                // Intentar leer el error como JSON
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error ?? errorData.message ?? `Error ${response.status}`);
            }

            const blob = await response.blob();

            // Extraer nombre de archivo del header Content-Disposition
            const disposition = response.headers.get('Content-Disposition') ?? '';
            const match       = disposition.match(/filename="(.+?)"/);
            const filename    = match?.[1] ?? `leads_${new Date().toISOString().slice(0, 10)}`;

            return { blob, filename };
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error(`Timeout: La petición tardó más de ${timeout}ms`);
                }
                throw error;
            }
            throw new Error('Error de conexión');
        }
    }

    private handleAuthError() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/') {
            window.location.href = '/';
        }
    }

    private handleServerError(response: Response) {
        console.log(response);
        if (window.location.pathname !== '/error') {
            window.location.href = `/error`;
        }
    }

    async get<T = any>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'GET' });
    }

    async post<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async patch<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            ...config,
            method: 'PATCH',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T = any>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, method: 'DELETE' });
    }

    async uploadFile<T = any>(
        endpoint: string,
        file: File,
        additionalData?: Record<string, any>,
        config?: RequestConfig & { timeout?: number }
    ): Promise<ApiResponse<T>> {
        const formData = new FormData();
        formData.append('file', file);

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        const token = localStorage.getItem('token');
        const headers: HeadersInit = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        return this.request<T>(endpoint, {
            ...config,
            method: 'POST',
            body: formData,
            headers,
            timeout: config?.timeout || 40000,
        });
    }

    async requestWithoutErrorRedirect<T = any>(endpoint: string, config: RequestConfig = {}): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { ...config, skipErrorRedirect: true });
    }

    setDefaultTimeout(timeout: number): void {
        this.defaultTimeout = timeout;
    }
}

export const apiClient = new ApiClient();